import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProjectTimezone } from 'store/project';

import { ContractOnView } from 'shared/domain/contract/types/view';
import { ContractColumnsFactory } from './columns';
import { useContractDataGridSlotProps } from './useContractDataGridSlotProps';
import { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import { sxStyles } from './styles';
import { useAppNavigation } from 'contexts/navigation';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  INITIAL_SORT_MODEL,
} from './constants';
import { DataGridInstanceName } from 'services/storage/keys';
import { SearchPhraseMismatchNoRowsOverlay } from 'components/common/dataGrid/noRowsOverlay';
import { DataGrid } from 'components/common/dataGrid';
import { createClientDataGridConfig } from 'components/common/dataGrid/config/DataGridConfig';

type ContractDataGridProps = {
  data: ContractOnView[];
  loading?: boolean;
  emptyListReason?: EmptyListReason;
};

export function ContractDataGrid({
  data,
  loading = false,
  emptyListReason,
}: ContractDataGridProps): ReactElement {
  const intl = useIntl();
  const timezone = useProjectTimezone();
  const { goToContractDetails } = useAppNavigation();

  const columns = useMemo(
    () => new ContractColumnsFactory(intl, timezone).create(),
    [intl, timezone]
  );

  const slotProps = useContractDataGridSlotProps({ columns });

  const config = useMemo(
    () =>
      createClientDataGridConfig<ContractOnView>(
        DataGridInstanceName.contract,
        INITIAL_SORT_MODEL
      )
        .withPagination({ defaultPageSize: DEFAULT_PAGE_SIZE, pageSizeOptions: PAGE_SIZE_OPTIONS })
        .withRowDoubleClick((params) => {
          if (params.row && params.row._id) {
            goToContractDetails(params.row._id);
          }
        })
        .withNoRowsOverlay(SearchPhraseMismatchNoRowsOverlay)
        .withSlotProps(slotProps)
        .withAutosizing()
        .build(),
    [goToContractDetails, slotProps]
  );

  return (
    <DataGrid
      data={data}
      columns={columns}
      config={config}
      loading={loading}
      emptyListReason={emptyListReason}
      styles={sxStyles}
    />
  );
}
